import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { UploadSummary, ValidatedGDORow, ColumnKey, COLUMN_KEYS, COLUMN_HEADERS, COLUMN_WIDTHS, getColumnComment } from './GDOBulkUploadTypes';

const NAVY_HEX = '003771';
const NAVY_DARK_HEX = '002B5C';

export function generateStatusReport(summary: UploadSummary): Blob {
  const workbook = XLSX.utils.book_new();

  const timestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss');

  const sheet1 = createUploadedSheet(summary.successRows, timestamp);
  XLSX.utils.book_append_sheet(workbook, sheet1, 'Uploaded GDOs');

  const sheet2 = createNotUploadedSheet(summary, timestamp);
  XLSX.utils.book_append_sheet(workbook, sheet2, 'Not Uploaded');

  const buffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    cellStyles: true,
  });

  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function generateInvalidEntriesTemplate(
  summary: UploadSummary,
  refData?: { sectors: string[]; markets: string[]; reports: string[] }
): Blob {
  const workbook = XLSX.utils.book_new();

  const timestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss');

  // Combine error rows and skipped rows
  const allInvalidRows = [
    ...summary.errorRows.map(({ row, reason }) => ({ row, reason })),
    ...summary.skippedRows.map((row) => ({ row, reason: 'Manually skipped' })),
  ];

  const sheet = createInvalidEntriesTemplateSheet(allInvalidRows, timestamp, refData);
  XLSX.utils.book_append_sheet(workbook, sheet, 'Invalid Entries Template');

  const buffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    cellStyles: true,
  });

  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

function createUploadedSheet(
  successRows: ValidatedGDORow[],
  timestamp: string
): XLSX.WorkSheet {
  const headers = [
    'GDO Name', 'Sector', 'Market', 'Report', 'Timezone', 'Owner',
    'GDO Description', 'Platform', 'Scheduler Name', 'Job Name',
    'ADF Factory Name', 'Workspace Name', 'Trigger Name', 'Application', 'Folder Path',
    'Schedule Days', 'Execution Start Time', 'Execution End Time', 'Avg Execution Time', 'Load Frequency',
    'SLA Type', 'SLA Days', 'SLA Start Time *', 'SLA End Time *',
    'SLA Criteria', 'Status',
  ];

  const data = [
    [`GDO Bulk Upload Status Report — Generated: ${timestamp}`],
    [],
    headers,
    ...successRows.map((row) => [...rowToArray(row), '✅ Uploaded']),
  ];

  const ws = XLSX.utils.aoa_to_sheet(data);

  styleHeaderRow(ws, headers.length);
  applyAltRowColors(ws, headers.length, 3);
  freezePanes(ws, 4);
  autoFitColumns(ws, headers.length);
  addTitleStyle(ws, headers.length);

  return ws;
}

function createNotUploadedSheet(
  summary: UploadSummary,
  timestamp: string
): XLSX.WorkSheet {
  const headers = [
    'GDO Name', 'Sector', 'Market', 'Report', 'Timezone', 'Owner',
    'GDO Description', 'Platform', 'Scheduler Name', 'Job Name',
    'ADF Factory Name', 'Workspace Name', 'Trigger Name', 'Application', 'Folder Path',
    'Schedule Days', 'Execution Start Time', 'Execution End Time', 'Avg Execution Time', 'Load Frequency',
    'SLA Type', 'SLA Days', 'SLA Start Time *', 'SLA End Time *',
    'SLA Criteria', 'Status', 'Reason',
  ];

  const errorRows = summary.errorRows.map(({ row, reason }) =>
    [...rowToArray(row), '❌ Error', reason]
  );

  const skippedRows = summary.skippedRows.map((row) =>
    [...rowToArray(row), '⏭ Skipped', 'Manually skipped']
  );

  const data = [
    [`GDO Bulk Upload Status Report — Generated: ${timestamp}`],
    [],
    headers,
    ...errorRows,
    ...skippedRows,
  ];

  const ws = XLSX.utils.aoa_to_sheet(data);

  styleHeaderRow(ws, headers.length);
  applyConditionalRowColors(ws, headers.length, 3);
  freezePanes(ws, 4);
  autoFitColumns(ws, headers.length);
  addTitleStyle(ws, headers.length);

  // Apply wrapText to the Reason column (last column) for multi-line error messages
  const reasonColIndex = headers.length - 1;
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let r = 3; r <= range.e.r; r++) {
    const cellRef = XLSX.utils.encode_cell({ r, c: reasonColIndex });
    if (ws[cellRef]) {
      ws[cellRef].s = ws[cellRef].s || {};
      ws[cellRef].s.alignment = { ...(ws[cellRef].s.alignment || {}), wrapText: true };
    }
  }

  return ws;
}

function createInvalidEntriesTemplateSheet(
  invalidRows: { row: ValidatedGDORow; reason: string }[],
  timestamp: string,
  refData?: { sectors: string[]; markets: string[]; reports: string[] }
): XLSX.WorkSheet {
  // Build headers with Remarks column first, then all other user columns
  // The Remarks column serves double duty: it shows error/skip reasons after upload
  const userColumnKeys = COLUMN_KEYS.filter((key) => key !== 'remarks');
  const headers = [
    COLUMN_HEADERS['remarks'],  // Remarks column first — shows error/skip reasons
    ...userColumnKeys.map((key) => COLUMN_HEADERS[key]),  // All other user columns
  ];

  // Build notes using the same getColumnComment function as the default template
  // This ensures consistent, detailed comments with dynamic values from refData
  const refDataForComments = refData || { sectors: [], markets: [], reports: [] };
  
  // Build notes in the same order as headers: remarks first, then user columns
  const notes = [
    'Error/skip reason — do not edit (system-populated)',  // Note for Remarks column
    ...userColumnKeys.map((key) => getColumnComment(key, refDataForComments)),  // Notes for user columns from template config
  ];

  // Build data rows: Remarks column first (with error/reason text), then all other user columns
  const data = [
    [`GDO Bulk Upload Template — Invalid Entries for Re-upload — Generated: ${timestamp}`],
    [],
    headers,
    ...invalidRows.map(({ row, reason }) => [
      reason || String(row['remarks'] || ''),  // Remarks column — show reason, fallback to existing remarks
      ...userColumnKeys.map((key) => String(row[key] || '')),  // All other user columns
    ]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(data);

  const totalCols = headers.length; // 80 columns (Remarks + 79 user columns)

  // Remarks column is now at index 0 in the headers array
  const remarksIdx = 0;

  // Add comments to header cells (row index 2)
  for (let c = 0; c < totalCols; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: 2, c });
    if (ws[cellRef] && notes[c]) {
      ws[cellRef].c = [{ a: 'Template', t: notes[c] }];
    }
  }

  // Style header row (row index 2)
  const headerStyle = {
    fill: { fgColor: { rgb: NAVY_HEX } },
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    alignment: { horizontal: 'center', wrapText: true, vertical: 'center' },
  };

  const mandatoryStyle = {
    ...headerStyle,
    fill: { fgColor: { rgb: NAVY_DARK_HEX } },
  };

  const remarksStyle = {
    ...headerStyle,
    fill: { fgColor: { rgb: 'E0E0E0' } },
    font: { color: { rgb: '999999' }, italic: true },
  };

  for (let c = 0; c < totalCols; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: 2, c });
    if (!ws[cellRef]) continue;
    if (c === remarksIdx) {
      ws[cellRef].s = remarksStyle;
    } else {
      ws[cellRef].s = mandatoryStyle;
    }
  }

  // Lock and grey out remarks column dynamically
  if (remarksIdx >= 0) {
    for (let r = 0; r < data.length; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: remarksIdx });
      if (!ws[cellRef]) ws[cellRef] = { v: r === 2 ? headers[remarksIdx] : '' };
      ws[cellRef].s = {
        fill: { fgColor: { rgb: 'E0E0E0' } },
        font: { color: { rgb: '999999' }, italic: true },
        locked: true,
        alignment: { horizontal: 'center', wrapText: true, vertical: 'center' },
      };
    }
  }

  ws['!protect'] = { password: '', selectLockedCells: true, selectUnlockedCells: true, formatCells: false };
  ws['!freeze'] = { xSplit: 0, ySplit: 3, topLeftCell: 'A4' };

  // Use COLUMN_WIDTHS for accurate column widths (convert px to character width)
  // Build widths in the same order as headers: remarks first, then user columns
  const colWidths = [
    { wch: 35 },  // Width for Remarks column (wide enough for error messages to wrap)
    ...userColumnKeys.map((key) => ({ wch: Math.round(COLUMN_WIDTHS[key] / 7) || 15 })),  // Widths for user columns
  ];
  ws['!cols'] = colWidths;

  return ws;
}

/** Core column subset for summary status reports (17 columns, excludes Status which is appended by callers) */const SUMMARY_COLUMN_KEYS: ColumnKey[] = [
  'gdoName', 'sector', 'market', 'report', 'timezone', 'owner',
  'gdoDescription', 'gdoCategory', 'platform', 'schedulerName', 'jobName',
  'factoryName', 'workspaceName', 'triggerName', 'application', 'folderPath', 'scheduleDays', 'executionStartTime', 'executionEndTime', 'averageExecutionTime', 'loadFrequency',
  'slaType', 'slaDays', 'slaStartTime', 'slaEndTime', 'slaCriteria',
];

/** Map a ValidatedGDORow to a string array using the given column keys */
function rowToArray(row: ValidatedGDORow, keys: ColumnKey[] = SUMMARY_COLUMN_KEYS): string[] {
  return keys.map((key) => String(row[key] || ''));
}

function styleHeaderRow(ws: XLSX.WorkSheet, colCount: number) {
  const headerRow = 2;
  for (let c = 0; c < colCount; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRow, c });
    if (!ws[cellRef]) continue;
    ws[cellRef].s = {
      fill: { fgColor: { rgb: NAVY_HEX } },
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', wrapText: true, vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: 'FFFFFF' } },
        bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
      },
    };
  }
}

function applyAltRowColors(ws: XLSX.WorkSheet, colCount: number, dataStartRow: number) {
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let r = dataStartRow; r <= range.e.r; r++) {
    const isEven = (r - dataStartRow) % 2 === 0;
    for (let c = 0; c < colCount; c++) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (!ws[cellRef]) continue;
      ws[cellRef].s = ws[cellRef].s || {};
      ws[cellRef].s.fill = {
        fgColor: { rgb: isEven ? 'FFFFFF' : 'F5F5F5' },
      };
    }
  }
}

function applyConditionalRowColors(ws: XLSX.WorkSheet, colCount: number, dataStartRow: number) {
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  const statusColIndex = colCount - 2;

  for (let r = dataStartRow; r <= range.e.r; r++) {
    const statusCellRef = XLSX.utils.encode_cell({ r, c: statusColIndex });
    const statusValue = ws[statusCellRef]?.v;

    let bgColor: string | null = null;
    if (statusValue === '❌ Error') bgColor = 'FFEBEE';
    else if (statusValue === '⏭ Skipped') bgColor = 'FFF3E0';

    if (bgColor) {
      for (let c = 0; c < colCount; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (!ws[cellRef]) continue;
        ws[cellRef].s = ws[cellRef].s || {};
        ws[cellRef].s.fill = { fgColor: { rgb: bgColor } };
      }
    }
  }
}

function freezePanes(ws: XLSX.WorkSheet, freezeRow: number) {
  ws['!freeze'] = {
    xSplit: 0,
    ySplit: freezeRow,
    topLeftCell: 'A' + (freezeRow + 1),
  };
}

function autoFitColumns(ws: XLSX.WorkSheet, colCount: number) {
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  const widths: number[] = new Array(colCount).fill(12);

  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = 0; c < colCount; c++) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      const cell = ws[cellRef];
      if (cell && cell.v != null) {
        const len = String(cell.v).length;
        widths[c] = Math.max(widths[c], Math.min(len + 2, 50));
      }
    }
  }

  ws['!cols'] = widths.map((w) => ({ wch: w }));
}

function addTitleStyle(ws: XLSX.WorkSheet, colCount: number) {
  const titleRef = 'A1';
  if (ws[titleRef]) {
    ws[titleRef].s = {
      font: { bold: true, sz: 14, color: { rgb: NAVY_HEX } },
      alignment: { horizontal: 'left' },
    };
  }

  if (!ws['!merges']) ws['!merges'] = [];
  ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });
}